import { useState } from "react";
import type { MarketplaceOrder } from "../types";
import { useLocale } from "../../../context/LanguageContext";

interface CancelOrderDialogProps {
  order: MarketplaceOrder;
  onCancel: (reason: string) => void;
  onClose: () => void;
  loading: boolean;
}

export function CancelOrderDialog({
  order,
  onCancel,
  onClose,
  loading,
}: CancelOrderDialogProps) {
  const [reason, setReason] = useState("");
  const { t } = useLocale();

  const userName = order.user
    ? `${order.user.firstName ?? ""} ${order.user.lastName ?? ""}`.trim() ||
      order.user.email ||
      order.userId.slice(-8)
    : order.userId.slice(-8);

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/40" onClick={onClose} />
      <div className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-card p-6 shadow-xl">
        <h3 className="text-lg font-semibold text-foreground">{t("cancelDialog.title")}</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("cancelDialog.message", {
            item: order.item?.name ?? t("common.unknown"),
            userName,
            koins: order.koinsSpent.toLocaleString(),
          })}
        </p>

        <div className="mt-4">
          <label className="mb-1.5 block text-sm font-medium text-foreground">
            {t("cancelDialog.reasonLabel")} <span className="text-destructive">*</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            placeholder={t("cancelDialog.reasonPlaceholder")}
            autoFocus
          />
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="h-9 rounded-md px-4 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            {t("cancelDialog.goBack")}
          </button>
          <button
            type="button"
            onClick={() => onCancel(reason)}
            disabled={!reason.trim() || loading}
            className="h-9 rounded-md bg-destructive px-4 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? t("cancelDialog.cancelling") : t("cancelDialog.cancelAndRefund")}
          </button>
        </div>
      </div>
    </>
  );
}
